import { useEffect, useState } from "react"
import { useQuery } from "urql"

import "../graphql/queries"

import { DEBOUNCE_DELAY_MS } from "@/constants/common"
import { GetTransferProductInventoryListDocument, type InventoryByProductItem } from "@/graphql/codegen/graphql"
import { DEFAULT_OFFSET, DEFAULT_PAGE_SIZE } from "@/stores/createSearchStore"
import useDebounce from "@/utils/useDebounce"

interface UseGetTransferProductInventoriesParams {
  warehouseAreaId?: string
  query: string
  aisleCodes: string[]
}

interface UseGetTransferProductInventoriesReturn {
  inventories: InventoryByProductItem[]
  isFetching: boolean
  loadMore: () => void
}

export const useGetTransferProductInventories = ({
  warehouseAreaId,
  query,
  aisleCodes,
}: UseGetTransferProductInventoriesParams): UseGetTransferProductInventoriesReturn => {
  const debouncedQuery = useDebounce(query, DEBOUNCE_DELAY_MS)

  const [offset, setOffset] = useState(DEFAULT_OFFSET)
  const [inventories, setInventories] = useState<InventoryByProductItem[]>([])
  const [hasMore, setHasMore] = useState(true)

  const [{ data, fetching }] = useQuery({
    query: GetTransferProductInventoryListDocument,
    variables: {
      input: {
        filter: {
          warehouseIds: warehouseAreaId ? [warehouseAreaId] : undefined,
          query: debouncedQuery,
          aisleCodes: aisleCodes.length ? aisleCodes : undefined,
        },
        limit: DEFAULT_PAGE_SIZE,
        offset,
      },
    },
    pause: !warehouseAreaId,
  })

  useEffect(() => {
    setOffset(DEFAULT_OFFSET)
    setInventories([])
    setHasMore(true)
  }, [warehouseAreaId, debouncedQuery, aisleCodes])

  useEffect(() => {
    const output = data?.inventory.listByProduct
    const page =
      output?.__typename === "InventoryByProductListSuccess" ? (output.items as InventoryByProductItem[]) : []

    setInventories((prev) => (offset === DEFAULT_OFFSET ? page : [...prev, ...page]))
    setHasMore(page.length === DEFAULT_PAGE_SIZE)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only a new query result should append or replace the page; `offset` is read to pick which and must not retrigger the effect.
  }, [data])

  const loadMore = () => {
    if (fetching || !hasMore) return
    setOffset((prev) => prev + DEFAULT_PAGE_SIZE)
  }

  return { inventories, isFetching: fetching, loadMore }
}
